export type CaptureTelemetryEvent =
  | "client.capture.started"
  | "client.capture.connected"
  | "client.capture.first_audio"
  | "client.capture.first_transcript"
  | "client.capture.completed"
  | "client.capture.failed"
  | "client.storage.degraded"
  | "client.provider.failed"
  | "client.transcript_not_displayed";

export type EntryTelemetryEvent =
  | "client.entry.viewed"
  | "client.entry.try_selected"
  | "client.entry.signin_selected"
  | "client.entry.first_recording_completed"
  | "client.sample.completed"
  | "client.sample.signup_selected"
  | "client.sample.account_converted";

export type CaptureTelemetry = {
  captureAttemptId: string;
  recordingId?: string;
  connectionState?: "connected" | "buffering" | "standing-by";
  provider?: "openai" | "deepgram" | "mock" | "dicta";
  category?: "optional-sidecar" | "canonical-live-text" | "finalisation";
  errorCode?: string;
  stream?: "source" | "analysis" | "buffer";
  sequence?: number;
  elapsedMs?: number;
  audioPacketsSent?: number;
  transcriptFramesReceived?: number;
  rowsDisplayed?: number;
};

/** Fire-and-forget, content-free capture diagnostics. */
export function sendCaptureTelemetry(
  event: CaptureTelemetryEvent,
  fields: CaptureTelemetry
): void {
  void fetch("/api/telemetry", {
    method: "POST",
    credentials: "same-origin",
    keepalive: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ event, ...fields })
  }).catch(() => undefined);
}

const ENTRY_SESSION_KEY = "dicta-entry-session";

function entrySessionId(): string {
  const current = sessionStorage.getItem(ENTRY_SESSION_KEY);
  if (current && /^[A-Za-z0-9-]{1,128}$/.test(current)) return current;
  const created = crypto.randomUUID();
  sessionStorage.setItem(ENTRY_SESSION_KEY, created);
  return created;
}

/** Content-free entry telemetry. Never includes identity or matter data. */
export function sendEntryTelemetry(
  event: EntryTelemetryEvent,
  entryPath: "homepage" | "sample" | "shared-review" = "homepage"
): void {
  void fetch("/api/telemetry", {
    method: "POST",
    credentials: "omit",
    keepalive: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      event,
      anonymousSessionId: entrySessionId(),
      entryPath
    })
  }).catch(() => undefined);
}
